/**
 * Least squares by Householder QR — never the normal equations.
 *
 * Forming XᵀX squares the condition number of the problem, so a regression with nearly
 * collinear predictors (or a degree-6 polynomial) loses half its digits before it starts.
 * Here the design matrix is factorised directly, X = QR, and the coefficients come from
 * back-substitution on R.
 *
 * Rank deficiency is handled the way R's `lm` does, by LINPACK's `dqrdc2`: a column whose
 * remaining norm falls below 1e-7 of its original norm is judged to be (numerically) a
 * linear combination of the columns before it, and is moved to the end and aliased
 * rather than allowed to blow up the others. Its coefficient is NaN.
 */

export interface QrFit {
  n: number;
  /** Number of columns supplied. */
  p: number;
  /** Numerical rank: how many columns were kept. */
  rank: number;
  /** True for a column judged to be a combination of earlier columns. */
  aliased: boolean[];
  /** Coefficients in the original column order; NaN where aliased. */
  coefficients: number[];
  fitted: number[];
  residuals: number[];
  /** Residual sum of squares. */
  sse: number;
  /** (XᵀX)⁻¹ in the original column order; NaN rows and columns where aliased. */
  covariance: number[][];
  /** Diagonal of the hat matrix. */
  leverage: number[];
  /** log det(XᵀX) over the kept columns, from the diagonal of R. */
  logDetGram: number;
}

/** The default `lm` tolerance. */
export const RANK_TOLERANCE = 1e-7;

function norm(values: Float64Array, from: number): number {
  let total = 0;
  for (let i = from; i < values.length; i++) {
    total += values[i] * values[i];
  }
  return Math.sqrt(total);
}

/** Apply the reflector stored in column `l` (and `aux`) to `target`, in place. */
function reflect(
  column: Float64Array,
  aux: number,
  l: number,
  target: Float64Array
): void {
  if (aux === 0) {
    return;
  }
  const saved = column[l];
  column[l] = aux;
  let dot = 0;
  for (let i = l; i < target.length; i++) {
    dot += column[i] * target[i];
  }
  const t = -dot / column[l];
  for (let i = l; i < target.length; i++) {
    target[i] += t * column[i];
  }
  column[l] = saved;
}

interface Factorisation {
  columns: Float64Array[];
  aux: number[];
  order: number[];
  rank: number;
}

/** Move the column at `l` to the end of every parallel list. */
function cycleToEnd(f: Factorisation, l: number, tracked: number[][]): void {
  const [column] = f.columns.splice(l, 1);
  f.columns.push(column);
  const [name] = f.order.splice(l, 1);
  f.order.push(name);
  for (const list of tracked) {
    const [value] = list.splice(l, 1);
    list.push(value);
  }
}

/** One Householder step on column `l`, applied to the columns after it. */
function householderStep(
  f: Factorisation,
  l: number,
  n: number,
  remaining: number[]
): void {
  const x = f.columns[l];
  let nrm = norm(x, l);
  if (nrm === 0) {
    return;
  }
  if (x[l] !== 0) {
    nrm = Math.sign(x[l]) * Math.abs(nrm);
  }
  for (let i = l; i < n; i++) {
    x[i] /= nrm;
  }
  x[l] += 1;
  for (let j = l + 1; j < f.columns.length; j++) {
    const target = f.columns[j];
    let dot = 0;
    for (let i = l; i < n; i++) {
      dot += x[i] * target[i];
    }
    const t = -dot / x[l];
    for (let i = l; i < n; i++) {
      target[i] += t * x[i];
    }
    remaining[j] = norm(target, l + 1);
  }
  f.aux[l] = x[l];
  x[l] = -nrm;
}

/** In-place Householder QR with dqrdc2's limited column pivoting. */
function factorise(
  input: readonly (readonly number[])[],
  n: number,
  tolerance: number
): Factorisation {
  const p = input.length;
  const columns = input.map((column) => Float64Array.from(column));
  const remaining = columns.map((column) => norm(column, 0));
  // A column of zeros has nothing to compare against; dqrdc2 uses 1 so it is aliased.
  const original = remaining.map((value) => (value === 0 ? 1 : value));
  const f: Factorisation = {
    columns,
    aux: new Array<number>(p).fill(0),
    order: Array.from({ length: p }, (_, j) => j),
    rank: p,
  };
  let kept = p;
  for (let l = 0; l < Math.min(n, p); l++) {
    // Cycle near-dependent columns to the end.
    while (l < kept && remaining[l] < original[l] * tolerance) {
      cycleToEnd(f, l, [original, remaining]);
      kept -= 1;
    }
    if (l >= kept) {
      break;
    }
    householderStep(f, l, n, remaining);
  }
  f.rank = Math.min(kept, n);
  return f;
}

/** Back-substitute R b = c for the leading rank × rank block. */
function backSubstitute(
  columns: readonly Float64Array[],
  rank: number,
  c: Float64Array
): number[] {
  const b = new Array<number>(rank).fill(0);
  for (let i = rank - 1; i >= 0; i--) {
    let value = c[i];
    for (let j = i + 1; j < rank; j++) {
      value -= columns[j][i] * b[j];
    }
    b[i] = value / columns[i][i];
  }
  return b;
}

/** R⁻¹ for the leading rank × rank upper-triangular block. */
function invertR(columns: readonly Float64Array[], rank: number): number[][] {
  const inverse = Array.from({ length: rank }, () =>
    new Array<number>(rank).fill(0)
  );
  for (let col = 0; col < rank; col++) {
    const e = new Float64Array(rank);
    e[col] = 1;
    const solved = backSubstitute(columns, rank, e);
    for (let row = 0; row < rank; row++) {
      inverse[row][col] = solved[row];
    }
  }
  return inverse;
}

/** Q's leading `rank` columns' row sums of squares: the hat-matrix diagonal. */
function hatDiagonal(f: Factorisation, n: number): number[] {
  const leverage = new Array<number>(n).fill(0);
  for (let k = 0; k < f.rank; k++) {
    const e = new Float64Array(n);
    e[k] = 1;
    for (let l = f.rank - 1; l >= 0; l--) {
      reflect(f.columns[l], f.aux[l], l, e);
    }
    for (let i = 0; i < n; i++) {
      leverage[i] += e[i] * e[i];
    }
  }
  return leverage;
}

/** log det(XᵀX) = 2 Σ log|Rᵢᵢ| over the kept columns. */
function logDetGram(f: Factorisation): number {
  let total = 0;
  for (let l = 0; l < f.rank; l++) {
    total += 2 * Math.log(Math.abs(f.columns[l][l]));
  }
  return total;
}

export function qrLeastSquares(
  columns: readonly (readonly number[])[],
  y: readonly number[],
  tolerance = RANK_TOLERANCE
): QrFit {
  const n = y.length;
  const p = columns.length;
  const f = factorise(columns, n, tolerance);
  const rank = f.rank;
  const qty = Float64Array.from(y);
  for (let l = 0; l < rank; l++) {
    reflect(f.columns[l], f.aux[l], l, qty);
  }
  const pivoted = backSubstitute(f.columns, rank, qty);
  const coefficients = new Array<number>(p).fill(Number.NaN);
  const aliased = new Array<boolean>(p).fill(true);
  for (let k = 0; k < rank; k++) {
    coefficients[f.order[k]] = pivoted[k];
    aliased[f.order[k]] = false;
  }
  // Residuals: Q applied to (0, …, 0, qty[rank…]).
  const resid = new Float64Array(n);
  for (let i = rank; i < n; i++) {
    resid[i] = qty[i];
  }
  for (let l = rank - 1; l >= 0; l--) {
    reflect(f.columns[l], f.aux[l], l, resid);
  }
  const residuals = Array.from(resid);
  const fitted = y.map((value, i) => value - residuals[i]);
  let sse = 0;
  for (const value of residuals) {
    sse += value * value;
  }

  const rInverse = invertR(f.columns, rank);
  const covariance = Array.from({ length: p }, () =>
    new Array<number>(p).fill(Number.NaN)
  );
  for (let a = 0; a < rank; a++) {
    for (let b = 0; b < rank; b++) {
      let total = 0;
      for (let k = Math.max(a, b); k < rank; k++) {
        total += rInverse[a][k] * rInverse[b][k];
      }
      covariance[f.order[a]][f.order[b]] = total;
    }
  }
  return {
    n,
    p,
    rank,
    aliased,
    coefficients,
    fitted,
    residuals,
    sse,
    covariance,
    leverage: hatDiagonal(f, n),
    logDetGram: logDetGram(f),
  };
}
